import { createRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import useClickOutside from '@frolight/hooks/useClickOutside';
//import useRoveFocus from '@frolight/hooks/useRoveFocus';

const Dropdown = ({ buttonContent, menuItems, alignEnd = false }) => {

  const [open, setOpen] = useState(false);
  const [focusedMenuItemIndex, setFocusedMenuItemIndex] = useState(0);
  const [menuItemRefs, setMenuItemRefs] = useState([]);

  const toggleButtonRef = useRef(null);

  const innerRef = useClickOutside(e => {
    if (open) {
      setOpen(false);
    }
  });

  const buttonKeyDownHandler = (e) => {
    switch (e.keyCode) {
      case 40: // Arrow down
        if (!open) {
          setOpen(true);
        } else {
          if (focusedMenuItemIndex < menuItems.length - 1) {
            setFocusedMenuItemIndex(focusedMenuItemIndex + 1);
          }
        }
        break;
      case 38: // Arrow up
        if (open) {
          if (focusedMenuItemIndex > 0) {
            setFocusedMenuItemIndex(focusedMenuItemIndex - 1);
          } else {
            setOpen(false);
          }
        }
        break;
      default:
        break;
    }
  }

  const buttonClickHandler = (e) => {
    setOpen(!open);
  }

  useEffect(() => {
    setFocusedMenuItemIndex(0);
  }, [open]);

  useEffect(() => {
    setMenuItemRefs(menuItemRefs => (
      Array(menuItems.length).fill().map((_, i) => menuItemRefs[i] || createRef())
    ))
  }, [menuItems]);

  useEffect(() => {
    const documentKeyDownHandler = (e) => {
      switch (e.keyCode) {
        case 40: // Arrow down
          if (!open) {
            setOpen(true);
          } else {
            if (focusedMenuItemIndex < menuItemRefs.length - 1) {
              setFocusedMenuItemIndex(focusedMenuItemIndex + 1);
            }
          }
          break;
        case 38: // Arrow up
          if (open) {
            if (focusedMenuItemIndex > 0) {
              setFocusedMenuItemIndex(focusedMenuItemIndex - 1);
            } else {
              setOpen(false);
              toggleButtonRef.current.focus();
            }
          }
          break;
        default:
          break;
      }
    }

    const isOpenAndCorrectlyFocused = () => {
      return (open && focusedMenuItemIndex >= 0 && focusedMenuItemIndex < menuItemRefs.length);
    }

    if (isOpenAndCorrectlyFocused()) {
      menuItemRefs[focusedMenuItemIndex].current.focus();
    }

    if (focusedMenuItemIndex >= 0 && focusedMenuItemIndex < menuItemRefs.length) {
      if (open) {
        menuItemRefs[focusedMenuItemIndex].current.addEventListener('keydown', documentKeyDownHandler);
      } else {
        menuItemRefs[focusedMenuItemIndex].current.removeEventListener('keydown', documentKeyDownHandler);
      }
    }

    return () => {
      if (isOpenAndCorrectlyFocused()) {
        if (menuItemRefs[focusedMenuItemIndex] && menuItemRefs[focusedMenuItemIndex].current) {
            menuItemRefs[focusedMenuItemIndex].current.removeEventListener('keydown', documentKeyDownHandler);
        }
      }
    }
  }, [focusedMenuItemIndex, menuItemRefs, open]);

  return (
    <div className={`dropdown${ alignEnd ? '  dropdown--align_end' : '' }`} ref={innerRef}>
      <button
        className="dropdown__toggle"
        ref={toggleButtonRef}
        aria-haspopup="true"
        aria-expanded={open ? true : false}
        onKeyDown={buttonKeyDownHandler}
        onClick={buttonClickHandler}
      >
        {buttonContent}
      </button>
      <div role="menu" hidden={open ? false : true} className="dropdown__menu">
        {
          menuItems.map((menuItem, i) => (
            <button
              className="dropdown__menu_item"
              key={`dropdown-${i}`}
              role="menuitem"
              ref={menuItemRefs[i]}
              onClick={menuItem.clickHandler}
              disabled={menuItem.disabled || false}
            >
                {menuItem.label}
            </button>
          ))
        }
      </div>
    </div>
  );
}

Dropdown.propTypes = {
  buttonContent: PropTypes.node.isRequired,
  menuItems: PropTypes.array.isRequired
}

export default Dropdown;
